import React, { useState, useEffect } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import CustomerService from "../services/CustomerService";
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Button } from './common';
import log from "../services/logger";

const FormSection = ({ title, children }) => (
  <div className="bg-white rounded-lg shadow-md p-6 mb-6">
    <h3 className="text-lg font-semibold text-gray-800 mb-4">{title}</h3>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {children}
    </div>
  </div>
);

const EditClient = () => {
  const [loading, setLoading] = useState(true);
  const { id: clientID } = useParams();
  const navigate = useNavigate();

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      customerCode: "",
      nameOfTheLegalEntity: "",
      city: "",
      address: "",
      pib: "",
      identificationNumber: "",
      contactPerson: "",
      contactNumber: "",
      email: "",
      isActive: 'true',
      comment: "",
      paymentCurrency: "",
    },
    validationSchema: Yup.object().shape({
      nameOfTheLegalEntity: Yup.string().required('Polje "Ime legalnog entiteta" je obavezno.'),
      city: Yup.string().required('Polje "Grad" je obavezno.'),
      address: Yup.string().required('Polje "Adresa" je obavezno.'),
      pib: Yup.string()
        .length(9, 'Polje "PIB" mora imati tačno 9 brojeva.')
        .matches(/^[0-9]+$/, 'Polje "PIB" mora sadržati samo brojeve.')
        .required('Polje "PIB" je obavezno.'),
      identificationNumber: Yup.string()
        .length(8, 'Polje "Matični broj" mora imati tačno 8 brojeva.')
        .matches(/^[0-9]+$/, 'Polje "Matični broj" mora sadržati samo brojeve.')
        .required('Polje "Matični broj" je obavezno.'),
      contactPerson: Yup.string().required('Polje "Kontakt osoba" je obavezno.'),
      contactNumber: Yup.string().required('Polje "Kontakt broj" je obavezno.'),
      email: Yup.string().email('Nevažeća email adresa.').required('Polje "Email" je obavezno.'),
      paymentCurrency: Yup.string().required('Polje "Valuta plaćanja" je obavezno.'),
    }),
    onSubmit: async values => {
      try {
        await CustomerService.update(clientID, values);
        toast.success('Uspešno ažuriran klijent!');
        navigate('/clients');
      } catch (error) {
        log.error(error);
        toast.error('Neuspešno ažuriranje!');
      }
    },
  });

  useEffect(() => {
    const fetchClient = async () => {
      try {
        setLoading(true);
        const response = await CustomerService.get(clientID);
        formik.setValues({
          customerCode: response.data.customerCode || "",
          nameOfTheLegalEntity: response.data.nameOfTheLegalEntity || "",
          city: response.data.city || "",
          address: response.data.address || "",
          pib: response.data.pib || "",
          identificationNumber: response.data.identificationNumber || "",
          contactPerson: response.data.contactPerson || "",
          contactNumber: response.data.contactNumber || "",
          email: response.data.email || "",
          isActive: response.data.isActive ? 'true' : 'false',
          comment: response.data.comment || "",
          paymentCurrency: response.data.paymentCurrency || "",
        });
      } catch (error) {
        toast.error('Greška pri učitavanju podataka o klijentu');
        log.error(error);
      } finally {
        setLoading(false);
      }
    };

    fetchClient();
  }, [clientID]);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-gray-900">Izmena Klijenta</h2>
          <p className="mt-1 text-sm text-gray-600">
            Izmenite informacije o klijentu
          </p>
        </div>

        <form onSubmit={formik.handleSubmit} className="space-y-6">
          <FormSection title="Osnovne Informacije">
            <div className="form-group">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Šifra Klijenta
              </label>
              <input
                type="text"
                name="customerCode"
                value={formik.values.customerCode}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              />
              {formik.touched.customerCode && formik.errors.customerCode && (
                <div className="text-red-500 text-xs mt-1">{formik.errors.customerCode}</div>
              )}
            </div>

            <div className="form-group">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Naziv Pravnog Lica
              </label>
              <input
                type="text"
                name="nameOfTheLegalEntity"
                value={formik.values.nameOfTheLegalEntity}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              />
              {formik.touched.nameOfTheLegalEntity && formik.errors.nameOfTheLegalEntity && (
                <div className="text-red-500 text-xs mt-1">{formik.errors.nameOfTheLegalEntity}</div>
              )}
            </div>

            <div className="form-group">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                PIB
              </label>
              <input
                type="text"
                name="pib"
                value={formik.values.pib}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              />
              {formik.touched.pib && formik.errors.pib && (
                <div className="text-red-500 text-xs mt-1">{formik.errors.pib}</div>
              )}
            </div>

            <div className="form-group">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Matični Broj
              </label>
              <input
                type="text"
                name="identificationNumber"
                value={formik.values.identificationNumber}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              />
              {formik.touched.identificationNumber && formik.errors.identificationNumber && (
                <div className="text-red-500 text-xs mt-1">{formik.errors.identificationNumber}</div>
              )}
            </div>
          </FormSection>

          <FormSection title="Adresa">
            <div className="form-group">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Grad
              </label>
              <input
                type="text"
                name="city"
                value={formik.values.city}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              />
              {formik.touched.city && formik.errors.city && (
                <div className="text-red-500 text-xs mt-1">{formik.errors.city}</div>
              )}
            </div>

            <div className="form-group">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Adresa
              </label>
              <input
                type="text"
                name="address"
                value={formik.values.address}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              />
              {formik.touched.address && formik.errors.address && (
                <div className="text-red-500 text-xs mt-1">{formik.errors.address}</div>
              )}
            </div>
          </FormSection>

          <FormSection title="Kontakt Informacije">
            <div className="form-group">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Kontakt Osoba
              </label>
              <input
                type="text"
                name="contactPerson"
                value={formik.values.contactPerson}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              />
              {formik.touched.contactPerson && formik.errors.contactPerson && (
                <div className="text-red-500 text-xs mt-1">{formik.errors.contactPerson}</div>
              )}
            </div>

            <div className="form-group">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Kontakt Telefon
              </label>
              <input
                type="text"
                name="contactNumber"
                value={formik.values.contactNumber}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              />
              {formik.touched.contactNumber && formik.errors.contactNumber && (
                <div className="text-red-500 text-xs mt-1">{formik.errors.contactNumber}</div>
              )}
            </div>

            <div className="form-group">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email
              </label>
              <input
                type="email"
                name="email"
                value={formik.values.email}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              />
              {formik.touched.email && formik.errors.email && (
                <div className="text-red-500 text-xs mt-1">{formik.errors.email}</div>
              )}
            </div>
          </FormSection>

          <FormSection title="Dodatne Informacije">
            <div className="form-group">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Valuta Plaćanja
              </label>
              <input
                type="text"
                name="paymentCurrency"
                value={formik.values.paymentCurrency}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              />
              {formik.touched.paymentCurrency && formik.errors.paymentCurrency && (
                <div className="text-red-500 text-xs mt-1">{formik.errors.paymentCurrency}</div>
              )}
            </div>

            <div className="form-group">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Status
              </label>
              <div className="flex items-center space-x-6">
                <label className="flex items-center space-x-2">
                  <input
                    type="radio"
                    name="isActive"
                    value="true"
                    checked={formik.values.isActive === 'true'}
                    onChange={formik.handleChange}
                    className="h-4 w-4 text-blue-600"
                  />
                  <span>Aktivan</span>
                </label>
                <label className="flex items-center space-x-2">
                  <input
                    type="radio"
                    name="isActive"
                    value="false"
                    checked={formik.values.isActive === 'false'}
                    onChange={formik.handleChange}
                    className="h-4 w-4 text-blue-600"
                  />
                  <span>Neaktivan</span>
                </label>
              </div>
            </div>

            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Komentar
              </label>
              <textarea
                name="comment"
                rows="4"
                value={formik.values.comment}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              />
            </div>
          </FormSection>

          <div className="flex justify-end space-x-4 pt-6">
            <Button
              type="button"
              variant="secondary"
              onClick={() => navigate('/clients')}
            >
              Odustani
            </Button>
            <Button
              type="submit"
              variant="primary"
            >
              Sačuvaj Izmene
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditClient;

